import {
  TextField,
  Typography,
  InputLabel,
  Link,
  styled,
  useTheme,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Stack } from '@mui/system';
import { UseMutationResult } from '@tanstack/react-query';
import { trackEmailPromptCompleted } from 'src/utils/AuthTracking';
import FlashpackButton from '../FlashpackButton/FlashpackButton';
import React, { Dispatch, SetStateAction } from 'react';
import { LoginStrings, SignInSource } from './SignIn';

interface SignInFormProps {
  requestWebsiteLoginMagicLink: UseMutationResult<boolean, Error, string, unknown>;
  email: string;
  setEmail: (email: string) => void;
  isEmailRequired: 'REQUIRED' | 'OPTIONAL';
  labels: LoginStrings;
  source: SignInSource;
  isSignUp?: boolean;
  consentedForMarketingOnSignUp?: boolean;
  setConsentedForMarketingOnSignUp?: Dispatch<SetStateAction<boolean>>;
  setActiveTab?: Dispatch<SetStateAction<number>>;
}

export const SignInForm: React.FC<SignInFormProps> = ({
  requestWebsiteLoginMagicLink,
  email,
  setEmail,
  isEmailRequired,
  source,
  labels = {
    title: 'Enter your email to join us or sign in',
    subtitle: '',
    signInText: 'Continue',
    skipText: 'Skip',
    verifyEmail: 'Check your email and use the magic link',
  },
  isSignUp,
  consentedForMarketingOnSignUp,
  setConsentedForMarketingOnSignUp,
  setActiveTab,
}) => {
  const theme = useTheme();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    requestWebsiteLoginMagicLink.mutate(email);
    trackEmailPromptCompleted(source, isEmailRequired);
    return false;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    requestWebsiteLoginMagicLink.reset();
    setEmail(e.target.value);
  };

  const disabled =
    requestWebsiteLoginMagicLink.isPending || requestWebsiteLoginMagicLink.isSuccess;

  const renderHelperText = () => {
    if (
      requestWebsiteLoginMagicLink.isError &&
      requestWebsiteLoginMagicLink.failureReason?.message ===
        'Email already exists. Please log in instead.'
    ) {
      return (
        <>
          Email already exists.{' '}
          <Typography
            component="span"
            sx={{
              display: 'inline',
              fontSize: 'inherit',
              fontFamily: 'inherit',
              fontWeight: 'inherit',
            }}
          >
            Please{' '}
            <Link
              component="button"
              onClick={() => {
                requestWebsiteLoginMagicLink.reset();
                setActiveTab?.(1);
              }}
              sx={{
                cursor: 'pointer',
                verticalAlign: 'baseline',
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'none',
                },
              }}
            >
              log in
            </Link>{' '}
            instead.
          </Typography>
        </>
      );
    }
    return requestWebsiteLoginMagicLink.failureReason?.message;
  };

  return (
    <>
      <Stack gap={'16px'}>
        <Title>{labels.title}</Title>
        {labels.subtitle && <Subtitle>{labels.subtitle}</Subtitle>}
      </Stack>
      <form onSubmit={handleSubmit}>
        <Stack gap={'32px'}>
          <Stack gap={'8px'}>
            <InputLabel
              sx={{ ...theme.typography['Body S'], margin: 0 }}
              htmlFor="signin-email"
            >
              Your Email*
            </InputLabel>
            <InputField
              required
              name="email"
              autoComplete="email"
              fullWidth
              type="email"
              variant="filled"
              placeholder="Enter..."
              value={email}
              onChange={handleEmailChange}
              disabled={disabled}
              error={requestWebsiteLoginMagicLink.isError}
              helperText={renderHelperText()}
              inputProps={{
                'data-testid': 'signin-email',
                style: { ...theme.typography['Body M'] },
              }}
              hiddenLabel
            />
            {isSignUp && (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={consentedForMarketingOnSignUp}
                    onChange={(e) => setConsentedForMarketingOnSignUp?.(e.target.checked)}
                  />
                }
                label="Join our mailing list to be the first to discover our latest offers and adventures"
              />
            )}
          </Stack>
          <FlashpackButton
            type="submit"
            fullWidth
            disabled={email == '' || disabled}
            data-testid="signin-submit"
          >
            {labels.signInText}
          </FlashpackButton>
        </Stack>
      </form>
      <LegalText>
        {`By continuing, you agree to Flash Pack's `}
        <PermissionsLink
          target="_blank"
          href="https://www.flashpack.com/terms-and-conditions/"
        >
          Terms of Use
        </PermissionsLink>{' '}
        and{' '}
        <PermissionsLink target="_blank" href="https://www.flashpack.com/privacy-policy/">
          Privacy Policy
        </PermissionsLink>
      </LegalText>
    </>
  );
};

const InputField = styled(TextField)(({ theme }) => ({
  '& .MuiFilledInput-underline:after': {
    borderBottom: 'none',
  },
  '& .MuiFilledInput-underline:before': {
    borderBottom: 'none',
  },
  '& .MuiFilledInput-underline:hover:not(.Mui-disabled):before': {
    borderBottom: 'none',
  },
  '& .MuiFilledInput-root': {
    borderRadius: '8px',
    backgroundColor: theme.palette.principal.grey30,
  },
  '& .MuiFilledInput-input': {
    borderRadius: '8px',
  },
}));

const Title = styled(Typography)({
  fontSize: '24px',
  fontWeight: 500,
  lineHeight: '120%',
  letterSpacing: '0%',
  textAlign: 'center',
});

const Subtitle = styled(Typography)(({ theme }) => ({
  ...theme.typography['Body M'],
  textAlign: 'center',
}));

const LegalText = styled(Typography)(({ theme }) => ({
  ...theme.typography['Body S'],
  color: theme.palette.principal.grey70,
  textAlign: 'center',
}));

export const PermissionsLink = styled(Link)(({ theme }) => ({
  color: theme.palette.principal.grey70,
  borderBottom: 'none',
  '&:hover': {
    fontWeight: 500,
    textDecoration: 'underline',
    borderBottom: 'none',
  },
}));
